import { Card, type CardProps } from '@chakra-ui/react';
import classNames from 'classnames';
import {
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useKibanaLogger } from '../../../../../shared/hooks/kibana-logger/kibana-logger';
import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../../PageConfigProvider/page-reducer';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { usePageState } from '../../PageState/PageStateProvider';
import { useRegisterSharedChartData } from '../../SharedReport/SharedReportDataContext';
import { ChartError } from '../ChartError';
import { ChartNotSupported } from '../ChartNotSupported';
import { ChartProvider } from '../ChartProvider';
import { ChartMenuDataProvider } from '../chart-menu/ChartMenuContext';
import { CustomTableChart } from '../Table/custom-table-chart';
import { TableChart } from '../Table/TableChart';
import { useProcessContentSync } from '../TextChart/utils/use-process-content';
import { getSegmentForChartTitle } from '../utils/get-segment-for-chart-title';
import styles from './Chart.module.css';
import { DataWrappedChart } from './DataWrappedChart';
import { isSpecialChartType, SpecialChart } from './SpecialChart';

const isSpecialTablePage = (revelioPageId: RevelioPageId): boolean => {
  return [
    RevelioPageId.TALENT_DISCOVERY,
    RevelioPageId.POSTING_DISCOVERY,
    RevelioPageId.COMPANY_SCREENER_V2,
    RevelioPageId.GEOGRAPHY_SCREENER_V2,
    RevelioPageId.ROLE_SCREENER_V2,
  ].includes(revelioPageId);
};

export type ChartProps = {
  editMode?: boolean;
  item: PositionedItem;
  headerSize?: 'sm' | 'md';
  primaryFilter?: PrimaryFilterConfig;
  variant?: 'default' | 'report';
  onClick?: MouseEventHandler<HTMLDivElement>;
} & CardProps;

export const Chart = (props: ChartProps) => {
  return (
    <ChartProvider
      item={props.item}
      variant={props.variant}
      onEditClick={props.onClick}
    >
      <ChartMenuDataProvider>
        <ChartCardErrorBoundary>
          <ChartRootSelector {...props} />
        </ChartCardErrorBoundary>
      </ChartMenuDataProvider>
    </ChartProvider>
  );
};

const ChartCardErrorFallback = () => (
  <ChartCard>
    <ChartError message="An error occurred while loading the chart. Please try refreshing the page." />
  </ChartCard>
);

const ChartCardErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { kibanaLogger } = useKibanaLogger();

  const handleError = useCallback(
    (error: Error) => {
      console.error('ChartErrorBoundary', error);

      if (kibanaLogger === undefined) return;

      const message = error.stack
        ? `ChartErrorBoundary: ${error.message}\n${error.stack}`
        : `ChartErrorBoundary: ${error.message}`;
      kibanaLogger.error(message, {
        context: 'ChartErrorBoundary',
      });
    },
    [kibanaLogger]
  );

  return (
    <ErrorBoundary
      FallbackComponent={ChartCardErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export const ChartCard = ({
  onClick,
  children,
  ...cardProps
}: PropsWithChildren<Pick<ChartProps, 'onClick'>> & CardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;

  return (
    <Card
      ref={cardRef}
      role="group"
      width="100%"
      {...(isGlobalAiChartPreview
        ? { h: 'auto', minH: 'min-content', flexShrink: 0 }
        : { height: '100%' })}
      variant="unstyled"
      data-testid="card"
      position="static"
      onClick={onClick}
      {...cardProps}
      className={classNames(styles.card, cardProps.className)}
    >
      {children}
    </Card>
  );
};

const ChartRootSelector = ({
  editMode,
  item,
  headerSize = 'md',
  primaryFilter,
  onClick,
  ...cardProps
}: ChartProps) => {
  const chartType = item?.plot?.chartType;

  const { revelioPageId } = useTabContentConfig();
  const title = item?.title ?? '';
  const metric = usePlotMetric();
  const primaryFilterId = primaryFilter?.id;
  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryFilterId);

  const { pageState } = usePageState();

  const segment = getSegmentForChartTitle(
    item,
    pageState.dimensionSourceOverrides
  );
  const processedTitle = useProcessContentSync({
    content: title,
    currentMetric: metric,
    segment,
    diagnosticsMode: false,
  });
  useRegisterSharedChartData(item.id, { title: processedTitle });
  /** Company Overview Check primary selection is company
   * if selection is not company (industr) show error card */
  if (
    revelioPageId === RevelioPageId.COMPANY_OVERVIEW_V2 &&
    primaryFilterState?.selections?.some(
      (selection) => selection?.levelId !== 'company'
    )
  ) {
    return (
      <ChartNotSupported onClick={onClick}>
        Please select a company to see its overview.
      </ChartNotSupported>
    );
  }

  if (!chartType) {
    return <ChartNotSupported onClick={onClick} />;
  }

  if (
    chartType === 'table' &&
    isSpecialTablePage(revelioPageId as RevelioPageId)
  ) {
    return (
      <TableChart
        item={item}
        primaryFilter={primaryFilter}
        title={processedTitle}
        onClick={onClick}
      />
    );
  }

  if (chartType === 'table') {
    return (
      <CustomTableChart primaryDimension={primaryFilterId} onClick={onClick} />
    );
  }

  if (chartType && isSpecialChartType(chartType)) {
    return (
      <SpecialChart
        editMode={editMode}
        item={item}
        headerSize={headerSize}
        primaryFilter={primaryFilter}
        chartTitle={processedTitle}
        metric={metric}
        onClick={onClick}
      />
    );
  }

  return (
    <DataWrappedChart
      item={item}
      editMode={editMode}
      chartType={chartType}
      headerSize={headerSize}
      chartTitle={processedTitle}
      primaryFilterId={primaryFilterId}
      secondaryDimension={item?.plot?.secondaryDimension}
      metric={metric}
      pageId={revelioPageId}
      onClick={onClick}
      {...cardProps}
    />
  );
};
